import React, { useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { getEnvVar } from 'utils/get-env-var';

import InlineLoader from 'components/_core/InlineLoader';
import { ModalBody } from 'components/_core/Modal';
import Axios from 'axios';
import Message from 'components/_core/Message';
import { tripleWhale, tripleWhaleLineItems } from 'utils/triple-whale';
import { pushConversion } from 'utils/push-conversion';

// How long to wait for Impact's click id before starting checkout without it.
// Impact generates it asynchronously and it can land after this screen mounts.
const CLICK_ID_GRACE_MS = 2500;

const Subscribe = () => {
    const plan = useSelector(state => state.user.join.plan);
    const email = useSelector(state => state.user?.userData?.email);
    const impactClickId = useSelector(state => state.impact.clickId);
    // a click id is only on its way if Impact has been handed an email to identify
    const impactPending = useSelector(state => !!state.impact.emailSet && !state.impact.clickId);
    const [error, setError] = useState();
    const [graceElapsed, setGraceElapsed] = useState(false);
    const hasStarted = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => setGraceElapsed(true), CLICK_ID_GRACE_MS);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        // Start checkout exactly once. This effect re-runs when the Impact click
        // id (or the email) resolves, and without this guard that second run
        // created a second Stripe session, fired a second InitiateCheckout
        // conversion to Meta + TikTok, and raced a second redirectToCheckout.
        if (hasStarted.current || (impactPending && !graceElapsed)) {
            return;
        }

        hasStarted.current = true;

        async function redirect() {
            try {
                // omit clickId entirely when there isn't one — interpolating it
                // blindly sent the literal string "undefined" to Stripe metadata
                const clickIdParam = impactClickId ? `&clickId=${encodeURIComponent(impactClickId)}` : '';

                const checkoutResp = await Axios.get(`${getEnvVar('API_BASE')}/checkout-id?plan=${encodeURIComponent(plan)}${clickIdParam}`);
                const sessionId = checkoutResp.data.sessionId;

                // hand the server's Meta event name + id to GTM so the browser tag
                // sends the same pair and Meta collapses the two into one
                pushConversion(checkoutResp.data.conversion);

                // Triple Whale: the checkout session id is the order id used by the
                // Purchase event and by the server-side order record, so send it
                // here too and let Triple Whale stitch the two events together.
                tripleWhale('checkoutStarted', { email, orderId: sessionId, lineItems: tripleWhaleLineItems(plan) });

                // stash plan + email so the success page can fire Purchase — redux
                // state is lost across the Stripe redirect round-trip
                try {
                    localStorage.setItem('tw_checkout', JSON.stringify({ plan, email }));
                } catch (e) {}

                const stripe = await loadStripe(getEnvVar('STRIPE_PUBLIC_KEY'));
                stripe.redirectToCheckout({ sessionId });
            } catch(err) {
                setError(err?.response?.data?.msg || 'An unexpected error occured');
            }
        }

        redirect();
    }, [plan, email, impactClickId, impactPending, graceElapsed]);

    if (error) {
        return <ModalBody><Message heading="An error occured" error small>{error}</Message></ModalBody>;
    }

    return <ModalBody><InlineLoader dark text="Redirecting to Checkout" /></ModalBody>;
};

Subscribe.displayName = 'Subscribe';

export default Subscribe;
